import {
  escapeHtml,
  formatMoney,
  getSDK,
  mediaResponseOptions,
  mediaSrcset,
  mediaUrl,
  ORIGINAL_QUERY_PARAM,
  pinSearchId,
  slimProducts,
  productUrl,
  recallSearchId,
  rememberSearchId,
  SEARCH_ID_PARAM,
} from './layers.js';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;
const REQUEST_TIMEOUT_MS = 8000;
const PRODUCTS_LIMIT = 5;
/* Department label above each product title, from `custom.audience` (vendor as fallback). */
const DEPARTMENT_LABELS = { MENS: 'Men', BIGMEN: 'Big Men', KIDS: 'Kids' };
const VENDOR_DEPARTMENTS = { 'jordan craig': 'MENS', 'jc big men': 'BIGMEN', 'jc kids': 'KIDS' };

/* Checkout add-ons (re:do package protection) sell well but aren't merchandise. */
export function isMerchandise(product) {
  return !/package_protection/i.test(String(product?.type || '')) && String(product?.vendor || '').toLowerCase() !== 're:do';
}

export function productDepartment(product) {
  const audience = String(product?.metafields?.custom?.audience || '').toUpperCase();
  if (DEPARTMENT_LABELS[audience]) return audience;
  return VENDOR_DEPARTMENTS[String(product?.vendor || '').toLowerCase()] || '';
}

/* Tile images, matched to the preload hints requested for these requests. */
export const TILE_IMAGE_WIDTHS = [360, 540, 720];
export const TILE_IMAGE_SIZES = '(min-width: 1000px) 200px, 34vw';
const TILE_RESPONSE_OPTIONS = mediaResponseOptions({ sizes: TILE_IMAGE_SIZES, widths: TILE_IMAGE_WIDTHS });

/** Suggestion text with the typed part marked, escaped for innerHTML. */
export function highlightMatch(text, typed) {
  const value = String(text || '');
  const needle = normalizePredictiveQuery(String(typed || '')).toLowerCase();
  const index = needle ? value.toLowerCase().indexOf(needle) : -1;
  if (index < 0) return escapeHtml(value);

  return `${escapeHtml(value.slice(0, index))}<mark>${escapeHtml(value.slice(index, index + needle.length))}</mark>${escapeHtml(value.slice(index + needle.length))}`;
}

const SEARCH_ICON = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg>';
const TRENDING_ICON = '<svg aria-hidden="true" focusable="false" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6-6 4 4 8-8"></path><path d="M15 7h6v6"></path></svg>';
const ARROW_ICON = '<svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m13 6 6 6-6 6"></path></svg>';

export function normalizePredictiveQuery(value) {
  return value.trim().replace(/\s+/g, ' ');
}

export function isPredictiveQuery(value) {
  return normalizePredictiveQuery(value).length >= MIN_QUERY_LENGTH;
}

export function nextOptionIndex(currentIndex, direction, optionCount) {
  if (optionCount === 0) return -1;
  if (currentIndex < 0) return direction > 0 ? 0 : optionCount - 1;

  return (currentIndex + direction + optionCount) % optionCount;
}

export function isLatestPredictiveResponse(sequence, currentSequence, requestedQuery, currentInputValue) {
  return sequence === currentSequence && requestedQuery === normalizePredictiveQuery(currentInputValue);
}

function isSafeRedirect(url) {
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Results-page URL. `searchId` hands off the search the quick search already
 * prepared; `originalQuery` is the typed text when a suggestion drives the
 * search, so Layers attributes the suggestion to what the shopper typed.
 */
export function buildSearchUrl(baseUrl, query, origin = 'https://example.com', { searchId, originalQuery } = {}) {
  const url = new URL(baseUrl, origin);
  const normalized = normalizePredictiveQuery(query);
  url.searchParams.set('q', normalized);

  const typed = normalizePredictiveQuery(originalQuery || '');
  if (typed && typed.toLowerCase() !== normalized.toLowerCase()) {
    url.searchParams.set(ORIGINAL_QUERY_PARAM, typed);
  }
  if (searchId) url.searchParams.set(SEARCH_ID_PARAM, searchId);
  return url;
}

const SearchInterfaceElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

function productTileMarkup(product, strings, { option = true } = {}) {
  const url = productUrl(product);
  const title = product.title || '';
  const image = product.featuredMedia || product.images?.[0];
  const soldOut =
    product.availableForSale === false ||
    (product.variants?.length > 0 && product.variants.every((variant) => !variant.availableForSale));

  const price = product.price;
  const compareAt = product.compareAtPrice;
  const onSale =
    compareAt != null && price != null && Number(compareAt.amount ?? compareAt) > Number(price.amount ?? price);
  const priceCents = price == null ? null : Math.round(Number(price.amount ?? price) * 100);
  const compareCents = onSale ? Math.round(Number(compareAt.amount ?? compareAt) * 100) : null;

  const imageMarkup = image?.url
    ? `<img src="${escapeHtml(mediaUrl(image.url, 540))}" srcset="${escapeHtml(mediaSrcset(image.url, TILE_IMAGE_WIDTHS, image.width))}" sizes="${TILE_IMAGE_SIZES}" alt="${escapeHtml(image.alt || title)}" width="200" height="300" fetchpriority="high">`
    : '';

  const priceMarkup = priceCents != null
    ? `<span class="predictive-search__product-price">
        <span class="${onSale ? 'text-on-sale' : ''}">${escapeHtml(formatMoney(priceCents))}</span>
        ${onSale ? `<s class="text-subdued">${escapeHtml(formatMoney(compareCents))}</s>` : ''}
      </span>`
    : '';

  const soldOutMarkup = soldOut
    ? `<span class="predictive-search__sold-out">${escapeHtml(strings.soldOutBadge || 'Sold out')}</span>`
    : '';

  const department = DEPARTMENT_LABELS[productDepartment(product)];
  const departmentMarkup = department
    ? `<span class="predictive-search__product-department">${escapeHtml(department)}</span>`
    : '';

  const optionAttributes = option ? 'data-search-option data-search-result-type="product" tabindex="-1"' : '';
  return `<a href="${escapeHtml(url)}" class="predictive-search__product" ${optionAttributes}>
    <span class="predictive-search__product-image layers-media${imageMarkup ? ' is-loading' : ''}">${imageMarkup}</span>
    ${departmentMarkup}
    <span class="predictive-search__product-title">${escapeHtml(title)}</span>
    ${soldOutMarkup}
    ${priceMarkup}
  </a>`;
}

/* Drops each tile's logo skeleton once its image has loaded (or failed). */
function watchTileImages(root) {
  root.querySelectorAll('.layers-media.is-loading img').forEach((image) => {
    const done = () => image.parentElement.classList.remove('is-loading');
    if (image.complete && image.naturalWidth > 0) done();
    else {
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    }
  });
}

function queryRowMarkup(query, { baseUrl, typedQuery }) {
  const url = buildSearchUrl(baseUrl, query, window.location.origin, {
    originalQuery: typedQuery,
    searchId: recallSearchId(query),
  }).toString();
  return `<a href="${escapeHtml(url)}" class="predictive-search__suggestion" data-search-option data-search-result-type="query" tabindex="-1">${SEARCH_ICON}<span>${highlightMatch(query, typedQuery)}</span></a>`;
}

export class SearchInterface extends SearchInterfaceElement {
  connectedCallback() {
    if (this.connected) return;

    this.connected = true;
    this.host = this.closest('header-search');
    this.form = this.querySelector('form[role="search"]');
    this.input = this.querySelector('input[name="q"]');
    this.field = this.querySelector('[data-search-field]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.predictiveSearch = this.querySelector('predictive-search');
    this.results = this.querySelector('[slot="results"]');
    this.status = this.querySelector('[data-search-status]');
    this.stateMessage = this.querySelector('[data-search-state-message]');
    this.stateCopy = this.querySelector('[data-search-state-copy]');
    this.stateSubmit = this.stateMessage?.querySelector('[type="submit"]');
    this.noResultsTemplate = this.querySelector('[data-search-no-results-template]');
    this.discovery = this.querySelector('[data-search-discovery]');
    this.discoverySequence = 0;
    this.headerSection = this.host?.closest('.shopify-section--header');
    this.listenerController = new AbortController();
    this.requestSequence = 0;
    this.state = 'closed';

    if (!this.host || !this.form || !this.input || !this.results || !this.noResultsTemplate) return;

    const listenerOptions = { signal: this.listenerController.signal };
    const captureOptions = { capture: true, signal: this.listenerController.signal };

    this.addEventListener('input', (event) => this.handleInput(event), captureOptions);
    this.addEventListener('keydown', (event) => this.handleKeydown(event), captureOptions);
    this.addEventListener('submit', (event) => this.handleSubmit(event), captureOptions);
    this.addEventListener('click', (event) => this.handleClick(event), listenerOptions);
    document.addEventListener('keydown', (event) => this.handleDialogKeydown(event), captureOptions);
    this.host.addEventListener('dialog:before-show', () => this.handleBeforeShow(), listenerOptions);
    this.host.addEventListener('dialog:after-show', () => this.handleAfterShow(), listenerOptions);
    this.host.addEventListener('dialog:before-hide', () => this.handleBeforeHide(), listenerOptions);
    this.host.addEventListener('dialog:after-hide', () => this.handleAfterHide(), listenerOptions);

    this.host.controls.forEach((control) => {
      ['pointerenter', 'pointerdown', 'focus', 'touchstart'].forEach((type) =>
        control.addEventListener(type, () => this.prefetchDiscovery(), { ...listenerOptions, passive: true }),
      );
      control.addEventListener(
        'click',
        (event) => {
          const rootNode = this.host.getRootNode();
          if (this.host.id && rootNode.querySelector(`#${CSS.escape(this.host.id)}`) !== this.host) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.lastOpener = control;
          this.savedScrollPosition ??= window.scrollY;
          const willOpen = !this.host.open;
          willOpen ? this.host.show() : this.host.hide();
          if (willOpen) queueMicrotask(() => this.input.focus({ preventScroll: true }));
        },
        listenerOptions,
      );
    });

    this.syncClearButton();
  }

  disconnectedCallback() {
    this.listenerController?.abort();
    this.clearTimers();
    this.headerSection?.classList.remove('shopify-section--search-open');
    this.connected = false;
  }

  handleBeforeShow() {
    this.savedScrollPosition ??= window.scrollY;
    this.headerSection?.classList.add('shopify-section--search-open');
    this.dataset.searchProvider = 'layers';
    this.host.dataset.searchProvider = 'layers';
    this.setState(isPredictiveQuery(this.input.value) ? 'loading' : 'empty');
  }

  /*
   * Trending searches + best sellers, loaded once. Prefetched when the shopper
   * hovers, focuses or presses a search control, so they're usually ready by
   * the time the window opens.
   */
  prefetchDiscovery() {
    if (this.discoveryLoaded || isPredictiveQuery(this.input.value)) return;
    this.discoveryLoaded = true;
    this.loadDiscovery();
  }

  async loadDiscovery() {
    if (!this.discovery) return;
    const sequence = ++this.discoverySequence;

    try {
      const sdk = await this.sdk();
      // A few extra products to backfill add-ons dropped by isMerchandise().
      const pagination = { page: 1, limit: PRODUCTS_LIMIT + 3 };
      const blockId = this.dataset.bestSellersBlock;
      const [trending, bestSellers] = await Promise.all([
        sdk.autocomplete({}).get(),
        blockId
          ? sdk.blocks({ blockId, transformBody: slimProducts() }).get({ pagination, responseOptions: TILE_RESPONSE_OPTIONS })
          : sdk
              .collection({ collectionHandle: this.dataset.bestSellersCollection || 'all', transformBody: slimProducts() })
              .get({
              sort: 'best_selling',
              pagination,
              forceHideOutOfStock: true,
              responseOptions: TILE_RESPONSE_OPTIONS,
            }),
      ]);
      if (sequence !== this.discoverySequence) return;

      const products = (bestSellers?.data?.products || []).filter(isMerchandise).slice(0, PRODUCTS_LIMIT);
      this.renderDiscovery(trending?.data?.matchedQueries || [], products);
    } catch (error) {
      if (sequence !== this.discoverySequence) return;
      console.error('Layers search discovery failed:', error);
      this.discoveryLoaded = false;
      this.discovery.hidden = true;
    }
  }

  renderDiscovery(trendingQueries, products) {
    const strings = this.themeStrings();
    const trending = trendingQueries
      .slice(0, 8)
      .map((item) => {
        const query = item.query_text ?? item;
        const url = buildSearchUrl(this.dataset.searchUrl, query, window.location.origin).toString();
        return `<a href="${escapeHtml(url)}" class="predictive-search__suggestion">${TRENDING_ICON}<span>${escapeHtml(query)}</span></a>`;
      })
      .join('');

    this.discovery.innerHTML = `<div class="predictive-search__results predictive-search__results--with-suggestions">
      ${trending
        ? `<div class="predictive-search__suggestions-group">
            <span class="predictive-search__category">${escapeHtml(this.dataset.trendingLabel || 'Trending searches')}</span>
            <div class="predictive-search__suggestions">${trending}</div>
          </div>`
        : ''}
      <div class="predictive-search__products-group">
        <span class="predictive-search__category">${escapeHtml(this.dataset.bestSellersLabel || 'Best sellers')}</span>
        <div class="predictive-search__products">${products.map((product) => productTileMarkup(product, strings, { option: false })).join('')}</div>
      </div>
    </div>`;
    watchTileImages(this.discovery);
    this.discovery.hidden = this.state !== 'empty';
  }

  handleAfterShow() {
    this.input.focus({ preventScroll: true });

    const normalizedQuery = normalizePredictiveQuery(this.input.value);
    const canReuseSettledResults =
      isPredictiveQuery(normalizedQuery) &&
      this.results.childElementCount > 0 &&
      this.settledQuery === normalizedQuery;

    if (canReuseSettledResults) {
      // handleBeforeShow() set 'loading'; restore the retained response's state.
      this.setState(this.settledState || 'results');
    } else if (isPredictiveQuery(normalizedQuery)) {
      this.clearResults();
      this.input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText' }));
    } else {
      this.prefetchDiscovery();
    }
  }

  handleBeforeHide() {
    this.requestSequence += 1;
    this.clearTimers();
    this.resetActiveOption();
    this.setState('closed');
  }

  handleAfterHide() {
    if (Number.isFinite(this.savedScrollPosition)) {
      window.scrollTo({ top: this.savedScrollPosition, left: 0, behavior: 'auto' });
    }

    this.lastOpener?.focus({ preventScroll: true });
    this.headerSection?.classList.remove('shopify-section--search-open');
    this.savedScrollPosition = undefined;
  }

  handleDialogKeydown(event) {
    if (!this.host.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.host.hide();
      return;
    }

    if (event.key !== 'Tab' || !this.contains(event.target)) return;

    const stops = this.tabStops;
    if (stops.length < 2) return;

    const currentIndex = stops.indexOf(event.target);
    const direction = event.shiftKey ? -1 : 1;
    const nextIndex = currentIndex < 0 ? 0 : (currentIndex + direction + stops.length) % stops.length;

    event.preventDefault();
    event.stopImmediatePropagation();
    stops[nextIndex].focus({ preventScroll: true });
  }

  handleInput(event) {
    if (event.target !== this.input) return;

    const query = normalizePredictiveQuery(this.input.value);
    this.syncClearButton();
    this.resetActiveOption();
    this.clearTimers();
    event.stopPropagation();

    if (!isPredictiveQuery(query)) {
      this.requestSequence += 1;
      this.clearResults();
      this.setState('empty');
      return;
    }

    this.clearResults();
    this.setState('loading');

    const sequence = ++this.requestSequence;
    this.debounceTimer = window.setTimeout(() => this.searchLayers(query, sequence), SEARCH_DEBOUNCE_MS);
  }

  handleKeydown(event) {
    if (event.target !== this.input) return;

    const options = this.visibleOptions;
    const currentIndex = options.indexOf(this.activeOption);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.setActiveOption(options, nextOptionIndex(currentIndex, direction, options.length));
      return;
    }

    if (this.activeOption && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      event.stopPropagation();
      this.setActiveOption(options, event.key === 'Home' ? 0 : options.length - 1);
      return;
    }

    if (event.key === 'Enter' && this.activeOption) {
      event.preventDefault();
      event.stopPropagation();
      this.activeOption.click();
    }
  }

  handleClick(event) {
    const target = event.target;

    if (target.closest('[data-search-clear]')) {
      event.preventDefault();
      this.input.value = '';
      this.input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'deleteContentBackward' }));
      this.input.focus({ preventScroll: true });
      return;
    }

    if (target.closest('[data-search-field]') && !target.closest('button, input')) {
      this.input.focus({ preventScroll: true });
    }
  }

  handleSubmit(event) {
    if (normalizePredictiveQuery(this.input.value).length === 0) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.state === 'error') {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(this.completeSearchUrl());
    }
  }

  async searchLayers(query, sequence) {
    this.setState('loading');
    let timedOut = false;
    const requestTimer = window.setTimeout(() => {
      timedOut = true;
    }, REQUEST_TIMEOUT_MS);
    this.requestTimer = requestTimer;

    try {
      const sdk = await this.sdk();
      let raceTimer;
      const [autocompleteResult, searchResult] = await Promise.race([
        Promise.all([
          sdk.autocomplete({ query }).get(),
          // Remember the prepared search_id so the results page executes the
          // same search instead of preparing a new one.
          sdk
            .search({ query, transformBody: slimProducts(pinSearchId(null, (searchId) => rememberSearchId(query, searchId))) })
            .get({ pagination: { page: 1, limit: PRODUCTS_LIMIT }, responseOptions: TILE_RESPONSE_OPTIONS }),
        ]),
        new Promise((_, reject) => {
          raceTimer = window.setTimeout(
            () => reject(new Error('Layers request timed out')),
            REQUEST_TIMEOUT_MS + 50,
          );
        }),
      ]);
      window.clearTimeout(raceTimer);

      if (timedOut || !isLatestPredictiveResponse(sequence, this.requestSequence, query, this.input.value)) {
        return;
      }

      const redirect =
        autocompleteResult?.data?._meta?.redirect?.url || searchResult?.data?._meta?.redirect?.url;
      if (redirect && isSafeRedirect(redirect)) {
        window.location.assign(redirect);
        return;
      }

      const error = autocompleteResult?.error || searchResult?.error;
      if (error) {
        this.showError();
        return;
      }

      const matchedQueries = autocompleteResult?.data?.matchedQueries || [];
      const products = searchResult?.data?.products || [];
      this.renderResults(matchedQueries, products);
    } catch (error) {
      if (sequence !== this.requestSequence) return;
      console.error('Layers quick search failed:', error);
      this.showError();
    } finally {
      window.clearTimeout(requestTimer);
      if (this.requestTimer === requestTimer) this.requestTimer = undefined;

      if (sequence === this.requestSequence) {
        this.predictiveSearch.setAttribute('aria-busy', 'false');
      }
    }
  }

  async sdk() {
    if (!this.sdkPromise) this.sdkPromise = getSDK();
    return this.sdkPromise;
  }

  renderResults(matchedQueries, products) {
    const strings = this.themeStrings();

    if (matchedQueries.length === 0 && products.length === 0) {
      const templateContent = this.noResultsTemplate.content.cloneNode(true);
      templateContent.querySelector('.predictive-search__no-results').textContent = this.noResultsText;
      this.results.replaceChildren(templateContent);
      this.setState('no-results');
      this.settledQuery = normalizePredictiveQuery(this.input.value);
      this.settledState = 'no-results';
      this.announce(this.noResultsText);
      return;
    }

    const suggestions =
      matchedQueries.length > 0
        ? `<div class="predictive-search__suggestions-group" role="group" aria-label="${escapeHtml(this.dataset.suggestionsLabel || 'Suggestions')}">
            <span class="predictive-search__category">${escapeHtml(this.dataset.suggestionsLabel || 'Suggestions')}</span>
            <div class="predictive-search__suggestions">${matchedQueries
              .map((item) => queryRowMarkup(item.query_text ?? item, { baseUrl: this.dataset.searchUrl, typedQuery: this.input.value }))
              .join('')}</div>
          </div>`
        : '';

    const productRows =
      products.length > 0
        ? `<div class="predictive-search__products-group" role="group" aria-label="${escapeHtml(this.dataset.productsLabel || 'Products')}">
            <span class="predictive-search__category">${escapeHtml(this.dataset.productsLabel || 'Products')}</span>
            <div class="predictive-search__products">${products.map((product) => productTileMarkup(product, strings)).join('')}</div>
          </div>`
        : '';

    const results = document.createElement('div');
    results.className = `predictive-search__results${suggestions ? ' predictive-search__results--with-suggestions' : ''}`;
    results.setAttribute('role', 'listbox');
    results.dataset.searchResults = '';
    results.innerHTML = suggestions + productRows;

    this.results.replaceChildren(results);
    watchTileImages(this.results);
    this.enhanceOptions();
    this.renderViewAll();
    this.setState('results');
    this.settledQuery = normalizePredictiveQuery(this.input.value);
    this.settledState = 'results';
  }

  enhanceOptions() {
    this.results.querySelectorAll('[data-search-option]').forEach((option, index) => {
      option.id ||= `predictive-search-option-${index + 1}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.setAttribute('tabindex', '-1');
    });
  }

  renderViewAll() {
    const viewAll = document.createElement('a');
    viewAll.href = this.completeSearchUrl();
    viewAll.className = 'predictive-search__view-all';
    viewAll.dataset.searchViewAll = '';
    viewAll.slot = 'results';
    const label = this.dataset.viewAllForLabel
      ? this.dataset.viewAllForLabel.replace('__QUERY__', normalizePredictiveQuery(this.input.value))
      : this.dataset.viewAllLabel || 'View all results';
    viewAll.innerHTML = `<span>${escapeHtml(label)}</span>${ARROW_ICON}`;
    this.results.after(viewAll);
  }

  showError() {
    this.clearResults();
    this.setState('error');
    this.announce(this.dataset.unavailableText);
  }

  setState(state) {
    this.state = state;
    // Trending + best sellers show only while the query is empty.
    if (this.discovery?.childElementCount > 0) this.discovery.hidden = state !== 'empty';
    this.host.dataset.searchState = state;
    this.predictiveSearch.dataset.state = state;
    this.predictiveSearch.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');

    if (state === 'closed' || state === 'empty') {
      this.lastAnnouncement = undefined;
      this.status.textContent = '';
    }

    if (['loading', 'results'].includes(state)) {
      this.results.setAttribute('role', 'listbox');
    } else {
      this.results.removeAttribute('role');
    }

    this.input.setAttribute('aria-expanded', String(this.host.open && ['loading', 'results'].includes(state)));

    if (this.stateMessage) {
      this.stateMessage.hidden = !['loading', 'error'].includes(state);
      this.stateCopy.textContent = state === 'loading' ? this.dataset.searchingText : this.dataset.unavailableText;
      this.stateSubmit.hidden = state !== 'error';
    }
  }

  setActiveOption(options, index) {
    this.resetActiveOption();
    if (index < 0 || !options[index]) return;

    const option = options[index];
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  resetActiveOption() {
    this.results?.querySelectorAll('[data-search-option][aria-selected="true"]').forEach((option) => {
      option.setAttribute('aria-selected', 'false');
    });
    this.input?.removeAttribute('aria-activedescendant');
  }

  announce(message) {
    if (!message || this.lastAnnouncement === message) return;

    this.lastAnnouncement = message;
    this.status.textContent = '';
    window.requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }

  syncClearButton() {
    this.clearButton.hidden = this.input.value.length === 0;
  }

  clearResults() {
    if (this.results.childElementCount > 0) this.results.replaceChildren();
    this.predictiveSearch.querySelector(':scope > [data-search-view-all]')?.remove();
    this.settledQuery = undefined;
    this.resetActiveOption();
  }

  clearTimers() {
    window.clearTimeout(this.debounceTimer);
    window.clearTimeout(this.requestTimer);
    this.debounceTimer = undefined;
    this.requestTimer = undefined;
  }

  completeSearchUrl() {
    return buildSearchUrl(this.dataset.searchUrl, this.input.value, window.location.origin, {
      searchId: recallSearchId(normalizePredictiveQuery(this.input.value)),
    }).toString();
  }

  themeStrings() {
    try {
      return JSON.parse(document.getElementById('layers-theme-config')?.textContent || '{}').strings || {};
    } catch {
      return {};
    }
  }

  get visibleOptions() {
    return Array.from(this.results.querySelectorAll('[data-search-option]')).filter(
      (option) => !option.hidden && option.getClientRects().length > 0,
    );
  }

  get activeOption() {
    const id = this.input.getAttribute('aria-activedescendant');
    return id ? this.results.querySelector(`#${CSS.escape(id)}`) : null;
  }

  get tabStops() {
    return Array.from(
      this.querySelectorAll('input:not([type="hidden"]), button, a[href], [tabindex]:not([tabindex="-1"])'),
    ).filter(
      (element) =>
        !element.disabled &&
        !element.hidden &&
        !element.closest('[hidden]') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.getAttribute('tabindex') !== '-1',
    );
  }

  get noResultsText() {
    return this.dataset.noResultsText.replace('__QUERY__', this.input.value);
  }
}

if (typeof window !== 'undefined' && !window.customElements.get('search-interface')) {
  window.customElements.define('search-interface', SearchInterface);
}
